import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { AdminAPI } from '@shared/types/api';

// Event Ops Automation hooks (docs/EVENT_OPS_AUTOMATION.md §7).

const OPS_REFETCH_INTERVAL_MS = 10_000;

/** Ops overview tiles — polls every 10s while the page is visible so tiles stay live. */
export function useAdminOpsOverview(enabled = true) {
  return useQuery({
    queryKey: ['admin-ops-overview'],
    enabled,
    refetchInterval: OPS_REFETCH_INTERVAL_MS,
    queryFn: async (): Promise<AdminAPI.GetOpsOverviewResponse> => api.get('/admin/ops/overview'),
  });
}

export function useAdminOpsScopeDetail(eventTimeId: string, neighborhoodId: string, enabled = true) {
  return useQuery({
    queryKey: ['admin-ops-scope', eventTimeId, neighborhoodId],
    enabled: enabled && !!eventTimeId && !!neighborhoodId,
    refetchInterval: OPS_REFETCH_INTERVAL_MS,
    queryFn: async (): Promise<AdminAPI.GetOpsScopeDetailResponse> =>
      api.get(`/admin/ops/events/${eventTimeId}/${encodeURIComponent(neighborhoodId)}`),
  });
}

export interface AgentRunFilters {
  eventTimeId?: string;
  neighborhoodId?: string;
  status?: AdminAPI.AgentRunStatus;
}

export function useAdminAgentRuns(filters: AgentRunFilters = {}, enabled = true) {
  return useInfiniteQuery({
    queryKey: ['admin-ops-runs', filters],
    enabled,
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }): Promise<AdminAPI.GetAgentRunsResponse> => {
      const params = new URLSearchParams();
      if (filters.eventTimeId) params.set('eventTimeId', filters.eventTimeId);
      if (filters.neighborhoodId) params.set('neighborhoodId', filters.neighborhoodId);
      if (filters.status) params.set('status', filters.status);
      if (pageParam) params.set('before', pageParam);
      const qs = params.toString();
      return api.get(`/admin/ops/runs${qs ? `?${qs}` : ''}`);
    },
    getNextPageParam: (lastPage) => lastPage.data?.nextBefore ?? undefined,
  });
}

export function useAdminAgentRunDetail(runId: string | null) {
  return useQuery({
    queryKey: ['admin-ops-run', runId],
    enabled: !!runId,
    refetchInterval: OPS_REFETCH_INTERVAL_MS, // live narrative stream while a run is executing
    queryFn: async (): Promise<AdminAPI.GetAgentRunDetailResponse> =>
      api.get(`/admin/ops/runs/${runId}`),
  });
}

export interface AgentDecisionFilters {
  status?: 'pending' | 'approved' | 'rejected' | 'answered' | 'expired';
  eventTimeId?: string;
}

export function useAdminAgentDecisions(filters: AgentDecisionFilters = {}, enabled = true) {
  return useInfiniteQuery({
    queryKey: ['admin-ops-decisions', filters],
    enabled,
    refetchInterval: OPS_REFETCH_INTERVAL_MS,
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }): Promise<AdminAPI.GetAgentDecisionsResponse> => {
      const params = new URLSearchParams();
      if (filters.status) params.set('status', filters.status);
      if (filters.eventTimeId) params.set('eventTimeId', filters.eventTimeId);
      if (pageParam) params.set('before', pageParam);
      const qs = params.toString();
      return api.get(`/admin/ops/decisions${qs ? `?${qs}` : ''}`);
    },
    getNextPageParam: (lastPage) => lastPage.data?.nextBefore ?? undefined,
  });
}

/** Cross-run activity feed: runs with their audited actions, grouped per run. */
export function useOpsActivity(neighborhoodId?: string, eventTimeId?: string) {
  return useInfiniteQuery({
    queryKey: ['admin-ops-activity', neighborhoodId ?? 'all', eventTimeId ?? 'all'],
    refetchInterval: OPS_REFETCH_INTERVAL_MS,
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }): Promise<AdminAPI.GetOpsActivityResponse> => {
      const params = new URLSearchParams();
      // Generous pages: client-side filters (categories, hide no-ops) thin
      // the list, so small pages would force constant "load older" clicks.
      params.set('limit', '75');
      if (neighborhoodId) params.set('neighborhoodId', neighborhoodId);
      if (eventTimeId) params.set('eventTimeId', eventTimeId);
      if (pageParam) params.set('before', pageParam);
      const qs = params.toString();
      return api.get(`/admin/ops/activity${qs ? `?${qs}` : ''}`);
    },
    getNextPageParam: (lastPage) => lastPage.data?.nextBefore ?? undefined,
  });
}

/** Pending-decision count for the nav badge (same pattern as kiki flags). */
export function useOpsPendingDecisionCount(opts: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ['admin-ops-pending-count'],
    enabled: opts.enabled ?? true,
    refetchInterval: 60_000,
    queryFn: async (): Promise<number> => {
      const res: AdminAPI.GetAgentDecisionsResponse = await api.get(
        '/admin/ops/decisions?status=pending&limit=1'
      );
      return res.data?.pendingCount ?? 0;
    },
  });
}

function useInvalidateOps() {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: ['admin-ops-overview'] });
    queryClient.invalidateQueries({ queryKey: ['admin-ops-runs'] });
    queryClient.invalidateQueries({ queryKey: ['admin-ops-decisions'] });
    queryClient.invalidateQueries({ queryKey: ['admin-ops-pending-count'] });
    queryClient.invalidateQueries({ queryKey: ['admin-ops-scope'] });
  };
}

export function useOpsSweep() {
  const invalidate = useInvalidateOps();
  return useMutation({
    mutationFn: async (eventTimeId: string): Promise<AdminAPI.OpsSweepResponse> =>
      api.post('/admin/ops/sweep', { eventTimeId }),
    onSuccess: invalidate,
  });
}

export function useResumeAgentRun() {
  const invalidate = useInvalidateOps();
  return useMutation({
    mutationFn: async (params: {
      runId: string;
      instruction: string;
    }): Promise<AdminAPI.ResumeAgentRunResponse> => {
      const { runId, ...body } = params;
      return api.post(`/admin/ops/runs/${runId}/resume`, body);
    },
    onSuccess: invalidate,
  });
}

export function useResolveAgentDecision() {
  const invalidate = useInvalidateOps();
  return useMutation({
    mutationFn: async (params: {
      decisionId: string;
      resolution: 'approved' | 'rejected' | 'answered';
      answer?: string;
      note?: string;
    }): Promise<AdminAPI.ResolveAgentDecisionResponse> => {
      const { decisionId, ...body } = params;
      return api.post(`/admin/ops/decisions/${decisionId}/resolve`, body);
    },
    onSuccess: invalidate,
  });
}

export function useEnqueueAgentRun() {
  const invalidate = useInvalidateOps();
  return useMutation({
    mutationFn: async (
      params: AdminAPI.EnqueueAgentRunRequest
    ): Promise<AdminAPI.EnqueueAgentRunResponse> => api.post('/admin/ops/runs', params),
    onSuccess: invalidate,
  });
}

/** Run one pipeline stage for every neighborhood in an event (all its cities). */
export function useBulkEnqueueAgentRuns() {
  const invalidate = useInvalidateOps();
  return useMutation({
    mutationFn: async (
      params: AdminAPI.BulkEnqueueAgentRunsRequest & { eventTimeId: string }
    ): Promise<AdminAPI.BulkEnqueueAgentRunsResponse> => {
      const { eventTimeId, ...body } = params;
      return api.post(`/admin/ops/events/${eventTimeId}/runs`, body);
    },
    onSuccess: invalidate,
  });
}

export function useCancelAgentRun() {
  const invalidate = useInvalidateOps();
  return useMutation({
    mutationFn: async (runId: string): Promise<AdminAPI.CancelAgentRunResponse> =>
      api.post(`/admin/ops/runs/${runId}/cancel`, {}),
    onSuccess: invalidate,
  });
}

// ---------------------------------------------------------------------------
// Conductor conversation
//
// A turn is an agent run, so the answer does not come back in the POST
// response — it arrives when the run completes. The thread polls while
// anything is pending and falls idle once it isn't, which keeps a quiet
// console cheap.
// ---------------------------------------------------------------------------

export function useConductorThread(eventTimeId: string | null) {
  return useQuery({
    queryKey: ['admin', 'ops', 'conductor', eventTimeId],
    enabled: !!eventTimeId,
    queryFn: async (): Promise<AdminAPI.ConductorThreadResponse['data']> => {
      const res: AdminAPI.ConductorThreadResponse = await api.get(
        `/admin/ops/conductor/thread?eventTimeId=${encodeURIComponent(eventTimeId!)}`
      );
      return res.data;
    },
    // Poll fast while a turn is in flight, stop entirely when it lands.
    refetchInterval: (query) => (query.state.data?.pending ? 3000 : false),
  });
}

export function useSendConductorMessage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      params: AdminAPI.ConductorMessageRequest
    ): Promise<AdminAPI.ConductorMessageResponse> =>
      api.post('/admin/ops/conductor/messages', params),
    onSuccess: (_res, params) => {
      // Show the operator's own message immediately, and start polling for
      // the reply.
      queryClient.invalidateQueries({
        queryKey: ['admin', 'ops', 'conductor', params.eventTimeId],
      });
    },
  });
}
